import { Alert, Button, ButtonText, Checkbox, FormField, Spacer, TextInput, Typography } from '@percent/lemonade'
import styles from './ApplyModalContent.module.scss'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@mantine/core'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import {
  useAuthState,
  useCausesPortalAnalytics,
  useIsOrganisationInactive,
  useMutation
} from '@percent/cause-dashboard/common/hooks'
import { HELP_LINK } from '@percent/cause-dashboard/constants/support'
import {
  getSmartApplyPartnerApplyType,
  getSmartApplyPartnerConfigData,
  smartApplyPartners,
  SmartApplyPartner,
  SmartApplyPartnerConfigKey
} from '@percent/cause-dashboard/constants/smartApplyPartners'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'
import { useApplicationsCaptcha } from '@percent/cause-dashboard/common/hooks/useApplicationsCaptcha/useApplicationsCaptcha'
import { CaptchaWidget } from '@percent/cause-dashboard/common/components/captchaWidget/CaptchaWidget'
import { CaptchaSlowWarning } from '@percent/cause-dashboard/common/components/captchaWidget/CaptchaSlowWarning'
import { ApplyModalCommonProps, ApplyModalSource } from '../ApplyModalContainer'
import { productOffers } from '@percent/cause-dashboard/app/productOffers/data/productOffers'

enum ScreenState {
  Initial,
  Success,
  Error
}

const NameFieldsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`

type ApplyModalContentProps = ApplyModalCommonProps & {
  source: ApplyModalSource
  partner?: SmartApplyPartner
  experimentVariant?: string
  setIsApplied: (isApplied: boolean) => void
  onApplyExternal?: () => void
}

export function ApplyModalContent({
  modalTitle,
  modalDescription,
  disclaimer,
  products,
  source,
  partner,
  experimentVariant,
  externalApplyUrl,
  setIsApplied,
  onApplyExternal
}: Readonly<ApplyModalContentProps>) {
  const [checkedProducts, setCheckedProducts] = useState(products.map(product => ({ ...product, checked: true })))

  const { t } = useTranslation()
  const { authState } = useAuthState()
  const { track } = useCausesPortalAnalytics()
  const { storeService } = useServices()
  const isOrganisationInactive = useIsOrganisationInactive()
  const smartApplyPopupShown = useRef(false)
  const location = useLocation()
  const [_firstName, ..._lastNameParts] = authState.user?.fullName?.trim()
    ? authState.user.fullName.trim().split(' ')
    : ['']
  const showNameFields = !externalApplyUrl && (!_firstName || !_lastNameParts.join(' ').trim())

  const [firstName, setFirstName] = useState(_firstName)
  const [lastName, setLastName] = useState(_lastNameParts.join(' '))
  const [firstNameTouched, setFirstNameTouched] = useState(false)
  const [lastNameTouched, setLastNameTouched] = useState(false)

  const firstNameError =
    firstNameTouched && !firstName?.trim() ? t('form.isRequired', { name: 'First name' }) : undefined
  const lastNameError = lastNameTouched && !lastName?.trim() ? t('form.isRequired', { name: 'Last name' }) : undefined

  const nameIsValid = firstName?.trim().length > 0 && lastName?.trim().length > 0

  const requiresPhone =
    !externalApplyUrl &&
    checkedProducts.some(
      product =>
        (product.checked || !product.isOptional) &&
        smartApplyPartners[product.name as SmartApplyPartnerConfigKey]?.partnerFields.phone
    )
  const existingPhone = authState.user?.phoneNumber?.trim() ?? ''
  const showPhoneField = requiresPhone && !existingPhone

  const [phone, setPhone] = useState(existingPhone)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const phoneError = phoneTouched && !phone?.trim() ? t('form.isRequired', { name: 'Phone number' }) : undefined
  const phoneIsValid = !showPhoneField || phone?.trim().length > 0

  const isFormValid = nameIsValid && phoneIsValid && checkedProducts.some(product => product.checked)

  const [screenState, setScreenState] = useState<ScreenState>(ScreenState.Initial)

  const { recaptchaRef, getCaptchaToken, isVerifying, isSlow } = useApplicationsCaptcha()

  const [{ isLoading }, { apiFunc: createValidationSubmissions }] = useMutation(
    storeService.createValidationSubmissions,
    () => {
      setScreenState(ScreenState.Success)
      setIsApplied(true)
    },
    () => {
      setScreenState(ScreenState.Error)
    }
  )

  const handleProductCheckedValueChange = (product: typeof checkedProducts[number]) => {
    if (product.isOptional) {
      setCheckedProducts(prev => prev.map(p => (p.name === product.name ? { ...p, checked: !p.checked } : p)))
    }
  }

  const handleApplyButtonTextClicked = (name: typeof checkedProducts[number]['name']) => {
    const offerName = name === 'openai' ? 'openAi' : name
    const offer = productOffers[offerName]

    window.open(offer.primaryButton.link)
  }

  const handleApplyButtonClicked = async () => {
    const accountId = authState.user?.id as string
    const organisationId = authState.claimOrganisation?.id

    if (isOrganisationInactive) {
      setScreenState(ScreenState.Error)
      return
    }

    const appliedEventName =
      source === 'smart-apply' ? 'Smart Apply Modal Applied' : 'Product Offers Details Modal Applied'

    const variantForTracking =
      experimentVariant ?? 'partner_default_opt_out'

    track(appliedEventName, {
      accountId,
      organisationId,
      organisation: authState.claimOrganisation?.displayName,
      ...checkedProducts.reduce((acc, { name, checked }) => {
        acc[name] = checked
        return acc
      }, {} as Record<typeof checkedProducts[number]['name'], boolean>),
      ...(source === 'smart-apply' && partner ? { applyType: getSmartApplyPartnerApplyType(partner) } : {}),
      page: location.pathname,
      ...(variantForTracking ? { experimentVariant: variantForTracking } : {}),
      ...(partner === 'meta' ? { apply_type: 'external', meta: true } : {})
    })

    if (externalApplyUrl) {
      window.open(externalApplyUrl, '_blank', 'noopener,noreferrer')
      onApplyExternal?.()
      return
    }

    const partners = checkedProducts
      .filter(p => p.checked || !p.isOptional)
      .map(({ name }) => getSmartApplyPartnerConfigData(name as SmartApplyPartnerConfigKey))

    const resolvedPhone = existingPhone || phone.trim()

    createValidationSubmissions({
      accountId,
      organisationId,
      partners,
      names: { firstName: firstName.trim(), lastName: lastName.trim() },
      ...(requiresPhone && resolvedPhone ? { partnerFields: { phone: resolvedPhone } } : {}),
      source: 'smart_apply',
      experimentVariant: variantForTracking,
      token: await getCaptchaToken()
    })
  }

  useEffect(() => {
    if (!smartApplyPopupShown.current) {
      const trackModalOpened = async () => {
        const shownEventName =
          source === 'smart-apply' ? 'Smart Apply Modal Shown' : 'Product Offers Details Modal Shown'

        await track(shownEventName, {
          organisation: authState.claimOrganisation?.displayName,
          organisationId: authState.claimOrganisation?.id,
          page: location.pathname,
          accountId: authState.user?.id,
          ...(partner && { partner })
        })
      }
      void trackModalOpened()
      smartApplyPopupShown.current = true
    }
  }, [
    track,
    source,
    partner,
    authState.claimOrganisation?.displayName,
    authState.claimOrganisation?.id,
    authState.user?.id,
    smartApplyPopupShown,
    location.pathname
  ])

  return (
    <div className={styles.applyModalContentContainer}>
      <div className={styles.contentWrapper}>
        <Typography className={styles.title} variant="h2">
          {modalTitle}
        </Typography>

        {(isLoading || screenState === ScreenState.Initial) && (
          <>
            <div className={styles.subtitles}>
              <Typography variant="large">{modalDescription}</Typography>
            </div>

            <div className={styles.checkboxesContainer}>
              {checkedProducts.map(product => (
                <div
                  key={product.name}
                  className={styles.productCard}
                  {...(product.isOptional && {
                    role: 'button',
                    tabIndex: 0
                  })}
                  onClick={e => {
                    if ((e.target as HTMLElement).closest('input[type="checkbox"]')) {
                      return
                    }
                    handleProductCheckedValueChange(product)
                  }}
                  onKeyDown={e => {
                    if (!product.isOptional) return
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleProductCheckedValueChange(product)
                    }
                  }}
                >
                  <img src={product.logo} alt={product.displayName} className={styles.productLogo} />
                  <div className={styles.productInfo}>
                    <Typography variant="default-bold" className={styles.productTitle}>
                      {product.displayName}
                    </Typography>
                    <Typography $color="gray300" variant="default" className={styles.productDescription}>
                      {product.description}
                    </Typography>
                  </div>

                  {product.isOptional && (
                    <Checkbox
                      name={product.name}
                      variant="default"
                      label=""
                      onChange={() => handleProductCheckedValueChange(product)}
                      active={product.checked}
                      dataTestId={`${product.name}-checkbox`}
                      className={styles.productCheckbox}
                    />
                  )}
                </div>
              ))}

              {showNameFields && (
                <>
                  <span className={styles.separator} />
                  <Typography color="textSecondary" variant="default-bold">
                    {t(
                      checkedProducts.length === 1
                        ? 'typography.smartApply.weNeedYourNameSingular'
                        : 'typography.smartApply.weNeedYourName'
                    )}
                  </Typography>

                  <NameFieldsRow>
                    <FormField
                      label={t('form.firstName')}
                      status={firstNameError ? 'danger' : 'default'}
                      statusMessage={firstNameError}
                      necessity="required"
                    >
                      <TextInput
                        type="text"
                        name="firstName"
                        placeholder={t('form.firstNameDescription')}
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        onBlur={() => setFirstNameTouched(true)}
                        disabled={isLoading}
                      />
                    </FormField>
                    <FormField
                      label={t('form.lastName')}
                      status={lastNameError ? 'danger' : 'default'}
                      statusMessage={lastNameError}
                      necessity="required"
                    >
                      <TextInput
                        type="text"
                        name="lastName"
                        placeholder={t('form.lastNameDescription')}
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        onBlur={() => setLastNameTouched(true)}
                        disabled={isLoading}
                      />
                    </FormField>
                  </NameFieldsRow>
                </>
              )}

              {showPhoneField && (
                <>
                  <span className={styles.separator} />
                  <Typography color="textSecondary" variant="default-bold">
                    {t('typography.smartApply.weNeedYourPhone')}
                  </Typography>

                  <FormField
                    label={t('typography.smartApply.phoneNumber')}
                    status={phoneError ? 'danger' : 'default'}
                    statusMessage={phoneError}
                    necessity="required"
                  >
                    <TextInput
                      type="tel"
                      name="phone"
                      placeholder={t('typography.smartApply.phoneNumberPlaceholder')}
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      onBlur={() => setPhoneTouched(true)}
                      disabled={isLoading}
                    />
                  </FormField>
                </>
              )}
            </div>
          </>
        )}

        {!isLoading && screenState === ScreenState.Success && (
          <>
            <div className={styles.alertWrapper}>
              <Alert variant="success" title={t('typography.smartApply.success')} />
            </div>
            <Typography color="textSecondary" variant="default-bold" className={styles.whatHappensNowTitle}>
              {t('typography.smartApply.whatHappensNow.title')}
            </Typography>
            <Typography color="textSecondary" className={styles.hangingIndent}>
              {t('typography.smartApply.whatHappensNow.bullet1')}
            </Typography>
            <Typography color="textSecondary" className={styles.hangingIndent}>
              <Box
                dangerouslySetInnerHTML={{
                  __html: t('typography.smartApply.whatHappensNow.bullet2')
                }}
              />
            </Typography>
            <Typography color="textSecondary" className={styles.hangingIndent}>
              {t('typography.smartApply.whatHappensNow.bullet3')}
            </Typography>
            <Typography color="textSecondary" className={styles.remember}>
              {t('typography.smartApply.remember')}
            </Typography>
          </>
        )}

        {!isLoading && screenState === ScreenState.Error && (
          <>
            <div className={styles.alertWrapper}>
              <Alert variant="default" title={t('typography.smartApply.oops')} />
            </div>

            {checkedProducts.map(product => (
              <div key={product.name} className={styles.buttonTextWrapper}>
                <ButtonText
                  variant="primary"
                  icon="external-link"
                  onClick={() => handleApplyButtonTextClicked(product.name)}
                >
                  Apply for {t(`typography.smartApply.${product.name}.title`)}
                </ButtonText>
              </div>
            ))}
          </>
        )}
      </div>

      {(isLoading || screenState === ScreenState.Initial) && (
        <div className={`${styles.buttonWrapper} ${styles.applyButtonWrapper}`}>
          {disclaimer && <p className={styles.disclaimer}>{disclaimer}</p>}
          <Spacer size={4} axis="vertical" />
          <Button
            loading={isLoading || isVerifying}
            loadingText={t('button.applying')}
            variant="primary"
            stretch
            onClick={handleApplyButtonClicked}
            disabled={(!externalApplyUrl && !isFormValid) || isVerifying}
          >
            {t('button.apply')}
          </Button>
          <CaptchaSlowWarning isSlow={isSlow} />
        </div>
      )}

      {!isLoading && screenState === ScreenState.Success && (
        <div className={`${styles.buttonWrapper} ${styles.resultButtonWrapper}`}>
          <div className={styles.helpLink}>
            <Typography color="textSecondary">
              {t('typography.smartApply.helpCenter.text')}{' '}
              <a href={HELP_LINK} target="_blank" rel="noopener noreferrer">
                {t('typography.smartApply.helpCenter.urlText')}{' '}
              </a>
            </Typography>
          </div>
        </div>
      )}

      <CaptchaWidget ref={recaptchaRef} />
    </div>
  )
}
